import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getToken } from '../services/authService';
import { fetchLocations } from '../services/api';

const LocationContext = createContext();

const normalizeLocation = (location) => ({
    ...location,
    id: String(location.id),
    clientId: location.clientId != null ? String(location.clientId) : null,
});

/**
 * Fallback used only when a component is rendered outside the provider (for
 * example a narrow unit test). It intentionally contains no invented business
 * locations; real locations always come from /api/locations.
 */
export const useLocationContext = () => {
    const context = useContext(LocationContext);
    if (!context) {
        return {
            locations: [],
            currentLocation: null,
            currentLocationId: null,
            setCurrentLocationId: () => {},
            refreshLocations: async () => [],
            locationsLoading: false,
            locationsError: '',
            isOnline: true,
            syncQueueCount: 0,
        };
    }
    return context;
};

export const LocationProvider = ({ children }) => {
    const [locations, setLocations] = useState([]);
    const [currentLocationId, setCurrentLocationIdState] = useState(() => {
        try {
            return localStorage.getItem('sims_current_location_id') || null;
        } catch {
            return null;
        }
    });
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationsError, setLocationsError] = useState('');
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const [syncQueueCount, setSyncQueueCount] = useState(0);

    const setCurrentLocationId = useCallback((id) => {
        const nextId = id == null ? null : String(id);
        setCurrentLocationIdState(nextId);
        try {
            if (nextId) {
                localStorage.setItem('sims_current_location_id', nextId);
            } else {
                localStorage.removeItem('sims_current_location_id');
            }
        } catch {}
    }, []);

    const refreshLocations = useCallback(async () => {
        if (!getToken()) {
            setLocations([]);
            setLocationsError('');
            setLocationsLoading(false);
            return [];
        }

        setLocationsLoading(true);
        setLocationsError('');
        try {
            const data = await fetchLocations();
            const normalized = Array.isArray(data) ? data.map(normalizeLocation) : [];
            // Sort locations so that primary location (or Mississauga) is always at the top
            const sorted = [...normalized].sort((a, b) => {
                if (a.isPrimary && !b.isPrimary) return -1;
                if (!a.isPrimary && b.isPrimary) return 1;
                const aMiss = a.name?.toLowerCase().includes('mississauga') || a.code?.includes('MISSISSAUGA');
                const bMiss = b.name?.toLowerCase().includes('mississauga') || b.code?.includes('MISSISSAUGA');
                if (aMiss && !bMiss) return -1;
                if (!aMiss && bMiss) return 1;
                return (a.name || '').localeCompare(b.name || '');
            });
            setLocations(sorted);
            setCurrentLocationIdState((previousId) => {
                if (previousId === 'ALL') return 'ALL';
                const primaryLoc = sorted.find((l) => l.isPrimary || l.primary)
                    || sorted.find((l) => l.name?.toLowerCase().includes('mississauga') || l.code?.includes('MISSISSAUGA'))
                    || sorted[0];

                const currentLoc = sorted.find((l) => l.id === previousId);
                const isOldBramptonDefault = currentLoc && currentLoc.name?.toLowerCase().includes('brampton') && primaryLoc && !currentLoc.isPrimary;
                const stillExists = sorted.some((location) => location.id === previousId);

                // Default to primary location (Dosa Boyz Mississauga), replacing old accidental Brampton default
                const nextId = (stillExists && !isOldBramptonDefault)
                    ? previousId
                    : (primaryLoc ? primaryLoc.id : (sorted[0] ? sorted[0].id : null));
                try {
                    if (nextId) {
                        localStorage.setItem('sims_current_location_id', nextId);
                    } else {
                        localStorage.removeItem('sims_current_location_id');
                    }
                } catch {}
                return nextId;
            });
            return sorted;
        } catch (error) {
            setLocationsError(error.response?.data?.message || error.message || 'Unable to load locations');
            return [];
        } finally {
            setLocationsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshLocations();

        const handleAuthChanged = () => {
            if (getToken()) {
                refreshLocations();
            } else {
                setLocations([]);
                setLocationsError('');
                setLocationsLoading(false);
            }
        };

        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('sims-auth-changed', handleAuthChanged);
        window.addEventListener('storage', handleAuthChanged);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('sims-auth-changed', handleAuthChanged);
            window.removeEventListener('storage', handleAuthChanged);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [refreshLocations]);

    const currentLocation = currentLocationId === 'ALL'
        ? null
        : (locations.find((location) => location.id === currentLocationId)
            || locations.find((location) => location.isPrimary)
            || locations.find((location) => location.name?.toLowerCase().includes('mississauga'))
            || locations[0]
            || null);

    const value = {
        locations,
        currentLocation,
        currentLocationId: currentLocationId === 'ALL' ? 'ALL' : (currentLocation ? currentLocation.id : null),
        setCurrentLocationId,
        refreshLocations,
        locationsLoading,
        locationsError,
        isOnline,
        syncQueueCount,
        setSyncQueueCount,
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};

export default LocationContext;
